import {
    Notifications,
    Close,
    CheckCircle,
    Info,
    Warning,
    Error as ErrorIcon,
} from '@mui/icons-material';
import {
    Box,
    Popover,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Button,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    Badge,
    ListItemButton,
    ListItemIcon,
    Tooltip,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useNotifications, useNotificationsUnread } from 'hooks/useNotifications';
import { RootState } from 'redux/store';
import type { NotificationInterface } from 'types';

interface NotificationPanelProps {
    variant?: 'icon' | 'list-item';
    collapsed?: boolean;
}

const NotificationPanel = ({ variant = 'icon', collapsed = false }: NotificationPanelProps) => {
    const navigate = useNavigate();
    const user = useSelector((state: RootState) => state.user.entity);
    const userId = user?.id || 0;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    // Sidebar only needs the unread badge; icon/popover needs the full list
    const { data: isUnread = false } = useNotificationsUnread(userId);
    const {
        data: notifications = [],
        isLoading,
        isError,
    } = useNotifications(userId, { enabled: variant === 'icon' });

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleNotificationClick = (notification: NotificationInterface) => {
        if (notification.url) {
            navigate(notification.url);
            handleClose();
        }
    };

    const getTypeIcon = (type: string | null) => {
        switch (type) {
            case 'SUCCESS':
                return <CheckCircle color="success" />;
            case 'WARNING':
                return <Warning color="warning" />;
            case 'ERROR':
                return <ErrorIcon color="error" />;
            default:
                return <Info color="info" />;
        }
    };

    const getTypeColor = (type: string | null) => {
        switch (type) {
            case 'SUCCESS':
                return 'success';
            case 'WARNING':
                return 'warning';
            case 'ERROR':
                return 'error';
            default:
                return 'info';
        }
    };

    const open = Boolean(anchorEl);
    const id = open ? 'notification-popover' : undefined;

    // Render as list item for sidebar
    if (variant === 'list-item') {
        return (
            <Tooltip title={collapsed ? 'Notifications' : ''} placement="right">
                <ListItemButton
                    onClick={() => navigate('/visa-process/notifications')}
                    sx={{
                        borderRadius: collapsed ? 1.5 : 1,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        px: collapsed ? 1.5 : 2,
                        py: collapsed ? 1 : 0.25,
                        '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: collapsed ? 'auto' : 40,
                        }}
                    >
                        <Badge variant="dot" color="error" invisible={!isUnread}>
                            <Notifications fontSize="small" />
                        </Badge>
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary="Notifications" />}
                </ListItemButton>
            </Tooltip>
        );
    }

    // Default icon button variant
    return (
        <>
            <IconButton onClick={handleClick} aria-describedby={id}>
                <Badge variant="dot" color="error" invisible={!isUnread}>
                    <Notifications />
                </Badge>
            </IconButton>
            {renderPopover()}
        </>
    );

    function renderPopover() {
        return (
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 420,
                            maxWidth: '90vw',
                            maxHeight: '80vh',
                            mt: 1,
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        },
                    },
                }}
            >
                <Box>
                    <Box
                        sx={{
                            p: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Notifications
                        </Typography>
                        <IconButton size="small" onClick={handleClose}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto' }}>
                        {isLoading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 6,
                                }}
                            >
                                <CircularProgress size={32} />
                            </Box>
                        ) : isError ? (
                            <Alert severity="error" sx={{ m: 2 }}>
                                Failed to load notifications. Please try again.
                            </Alert>
                        ) : notifications.length === 0 ? (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 8,
                                    px: 3,
                                }}
                            >
                                <Notifications
                                    sx={{ fontSize: 56, color: 'text.secondary', mb: 2 }}
                                />
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                                    No notifications yet
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    We'll notify you when something important happens
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {notifications.map((notification, index) => (
                                    <Box key={notification.id}>
                                        <ListItem
                                            sx={{
                                                cursor: notification.url ? 'pointer' : 'default',
                                                bgcolor: 'transparent',
                                                '&:hover': {
                                                    bgcolor: notification.url
                                                        ? 'action.hover'
                                                        : 'transparent',
                                                },
                                                alignItems: 'flex-start',
                                                gap: 1.5,
                                                py: 1,
                                                px: 1.5,
                                                transition: 'background-color 0.2s',
                                            }}
                                            onClick={() => handleNotificationClick(notification)}
                                        >
                                            <Box
                                                sx={{
                                                    mt: 0.5,
                                                    flexShrink: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: '50%',
                                                    bgcolor:
                                                        notification.notification_type === 'SUCCESS'
                                                            ? 'success.lighter'
                                                            : notification.notification_type ===
                                                                'WARNING'
                                                              ? 'warning.lighter'
                                                              : notification.notification_type ===
                                                                  'ERROR'
                                                                ? 'error.lighter'
                                                                : 'info.lighter',
                                                }}
                                            >
                                                {getTypeIcon(notification.notification_type)}
                                            </Box>
                                            <ListItemText
                                                sx={{ flex: 1, m: 0 }}
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            lineHeight: 1.5,
                                                            fontWeight: 400,
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        {notification.message}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 1 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 0.75,
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            {notification.program_name && (
                                                                <Chip
                                                                    label={
                                                                        notification.program_name
                                                                    }
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{
                                                                        height: 22,
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 500,
                                                                    }}
                                                                />
                                                            )}
                                                            {notification.client_status && (
                                                                <Chip
                                                                    label={
                                                                        notification.client_status
                                                                    }
                                                                    size="small"
                                                                    color={getTypeColor(
                                                                        notification.notification_type
                                                                    )}
                                                                    sx={{
                                                                        height: 22,
                                                                        fontSize: '0.7rem',
                                                                        fontWeight: 500,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            {formatDistanceToNow(
                                                                notification.created_at
                                                            )}
                                                            {notification.firstname &&
                                                                notification.lastname && (
                                                                    <>
                                                                        {' • '}
                                                                        <span>
                                                                            {notification.firstname}{' '}
                                                                            {notification.lastname}
                                                                        </span>
                                                                    </>
                                                                )}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        {index < notifications.length - 1 && (
                                            <Divider sx={{ mx: 2.5 }} />
                                        )}
                                    </Box>
                                ))}
                            </List>
                        )}
                    </Box>

                    {notifications.length > 0 && (
                        <Box
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                textAlign: 'center',
                                bgcolor: 'background.default',
                            }}
                        >
                            <Button
                                size="small"
                                fullWidth
                                onClick={() => {
                                    navigate('/visa-process/notifications');
                                    handleClose();
                                }}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                View all notifications
                            </Button>
                        </Box>
                    )}
                </Box>
            </Popover>
        );
    }
};

export default NotificationPanel;
